<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';

import SideMainBarToggleBtn from '@/components/btns/SideMainBarToggleBtn.vue';
import LogoImgLink from '@/components/imgLinks/LogoImgLink.vue';
import SideMainBarPortalVersionText from '@/components/texts/SideMainBarPortalVersionText.vue';
import KTooltip from '@/components/tooltips/KTooltip.vue';
import SmartMainSideBarMenuVList from '@/components/vLists/SmartMainSideBarMenuVList.vue';
import { useHotkeysStore } from '@/stores/useHotkeysStore';
import * as hotkeys from '@/constants/hotkeys';
import * as routeNames from '@/constants/routeNames';

export interface Props {
  modelValue?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const hotkeysStore = useHotkeysStore();

function handleClick(): void {
  emit('update:modelValue', !props.modelValue);
}

function handleToggleMainSideBarHotkey(event: KeyboardEvent) {
  handleClick();
}

onMounted(() => {
  hotkeysStore.add(
    hotkeys.TOGGLE_MAIN_SIDE_BAR,
    handleToggleMainSideBarHotkey,
    {},
    hotkeys.comments.TOGGLE_MAIN_SIDE_BAR,
  );
});

onUnmounted(() => {
  hotkeysStore.remove(hotkeys.TOGGLE_MAIN_SIDE_BAR);
});
</script>

<template>
  <v-navigation-drawer
    :elevation="0"
    permanent
    :rail-width="parseInt($style.railWidth)"
    :width="240"
    :rail="!props.modelValue"
    :class="[$style.mainSideBar, { [$style.isMini]: !props.modelValue }]"
  >
    <div :class="$style.sidebarItems">
      <div :class="$style.topItem">
        <LogoImgLink
          :class="$style.logoImgLink"
          :is-mini="!props.modelValue"
          :to="{ name: routeNames.DEFAULT }"
        />
        <SideMainBarPortalVersionText :is-mini="!props.modelValue" />
      </div>
      <div :class="$style.listItem">
        <SmartMainSideBarMenuVList :is-mini="!props.modelValue" />
      </div>
      <div :class="$style.bottomItem">
        <KTooltip location="right" :offset="-5">
          <div>
            {{ hotkeys.comments.TOGGLE_MAIN_SIDE_BAR }}
            <v-hotkey inline :keys="hotkeys.TOGGLE_MAIN_SIDE_BAR" />
          </div>
          <template #activator="{ props: tooltipProps }">
            <SideMainBarToggleBtn
              v-bind="tooltipProps"
              :is-mini="!props.modelValue"
              @click="handleClick"
            />
          </template>
        </KTooltip>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

$railWidth: variables.$sideMainBarWidth;

:export {
  railWidth: $railWidth;
}

.mainSideBar {
  background-color: colors.$color-brand-50 !important;
  border-right: 1px solid colors.$color-brand-100 !important;
}
  .sidebarItems {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
  }
    .topItem {
      border-bottom: 1px solid colors.$color-brand-100;
      height: variables.$appBarHeight + 1px;
      flex-shrink: 0;

      .isMini & {
        width: $railWidth;
      }
    }
      .logoImgLink {
        padding-left: 22px;
        padding-top: 13px;
        display: flex;
        justify-content: normal;
      }
      .isMini .logoImgLink {
        padding-left: 0px;
        justify-content: center;
        text-align: center;
      }
    .listItem {
      margin-top: 0px;

      .isMini & {
        width: $railWidth;
      }
    }
    .bottomItem {
      margin-top: auto;
      border-top: 1px solid colors.$color-brand-100;
    }
</style>
